// Shared shapes for the Executive Challenge backend. Both implementations
// (local stub, Supabase) must speak these exact types so the active backend
// can be swapped by config without touching any caller.

export type ChallengeRow = {
	code: string;
	seed: string;
	tid: number;
};

export type ChallengeEntry = {
	code: string;
	playerId: string;
	gmScore: number;
	grade: string;
	draftPick: number | null;
	wins: number;
	losses: number;
};

// One class shared by every implementation: callers distinguish user-facing
// join-code problems from infrastructure failures by this identity, so the
// stub and the real backend must throw literally the same class.
export class ChallengeCodeError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "ChallengeCodeError";
	}
}

// The contract each implementation fulfills. `requestOtp`/`verifyOtp` exist so
// the verify sheet can drive a real phone check when one is configured; the
// stub accepts anything (Phase 1: verification is theater until launch).
// getVerifiedSession's playerId is the identity's saved handle (Supabase:
// user_metadata.player_id) so a returning phone gets its initials prefilled on
// any device; setPlayerId pins it after the OTP step, when the user picks one.
export type ChallengeBackend = {
	getVerifiedSession: () => Promise<{ uid: string; playerId?: string } | null>;
	requestOtp: (phone: string) => Promise<void>;
	verifyOtp: (args: {
		phone: string;
		token: string;
		playerId?: string;
	}) => Promise<{ uid: string }>;
	setPlayerId: (playerId: string) => Promise<void>;
	createChallengeRow: (args: {
		tid: number;
		scenarioId: string;
		hostPlayerId: string;
	}) => Promise<ChallengeRow>;
	getChallengeRow: (code: string) => Promise<ChallengeRow>;
	submitEntry: (
		entry: ChallengeEntry,
	) => Promise<{ shareId: string; shareExpiresAt: number }>;
	getLeaderboard: (code: string) => Promise<ChallengeEntry[]>;
};
